import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";

const plans = [
  {
    name: "Professional",
    tag: "For ATOs",
    price: "Contact",
    sub: "for ATO pricing",
    desc: "For Aviation Training Organizations",
    features: [
      "G1000NXi Add-On for Prepar3D",
      "Full PFD + MFD simulation",
      "Synthetic Vision Technology (SVT)",
      "LNAV / VNAV 3D approach",
      "Full failure injection — all systems",
      "FAA / EASA certification support",
      "Custom branding & labeling",
      "Multi-display configuration",
      "Reversionary mode simulation",
      "Technical support & updates",
    ],
    cta: "Get Professional License",
    highlight: true,
  },
  {
    name: "Enterprise",
    tag: "Custom",
    price: "Custom",
    sub: "fully tailored",
    desc: "Full cockpit & custom avionics solutions",
    features: [
      "Everything in Professional",
      "Custom aircraft cockpit simulation",
      "Type-specific avionics development",
      "Custom instrument panels",
      "CBT software integration",
      "Prepar3D airport development",
      "Dedicated project manager",
      "Source code delivery options",
    ],
    cta: "Discuss Enterprise",
    highlight: false,
  },
];

export function PricingSection() {
  const go = () => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="pricing" className="py-24 bg-secondary/30">
      <div className="container mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ type: "spring", stiffness: 280, damping: 32 }} className="mb-12">
          <p className="text-xs font-mono tracking-[0.25em] uppercase font-bold mb-2" style={{ color: "oklch(0.55 0.22 25)" }}>— Licensing —</p>
          <h2 className="text-4xl md:text-5xl font-black text-foreground" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Choose Your <span style={{ color: "oklch(0.28 0.09 240)" }}>Plan</span>
          </h2>
          <p className="text-muted-foreground mt-2 max-w-lg">
            Flexible licensing for Aviation Training Organizations and enterprise clients.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
          {plans.map((plan, i) => (
            <motion.div key={plan.name} initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.1, type: "spring", stiffness: 280, damping: 32 }}
              className={`relative bg-white rounded-xl border p-8 flex flex-col ${plan.highlight ? "border-primary shadow-[0_8px_40px_oklch(0.28_0.09_240/0.18)]" : "border-border shadow-[0_2px_12px_oklch(0.12_0.03_240/0.06)]"}`}
              style={plan.highlight ? { borderLeft: "5px solid oklch(0.28 0.09 240)" } : { borderLeft: "5px solid oklch(0.88 0.01 240)" }}>
              <div className="flex items-start justify-between mb-5">
                <div>
                  <h3 className="text-xl font-black text-foreground" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{plan.name}</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">{plan.desc}</p>
                </div>
                <span className="text-xs font-semibold font-mono tracking-wider px-3 py-1 rounded-full bg-secondary border border-border text-muted-foreground">
                  {plan.tag}
                </span>
              </div>

              <div className="flex items-baseline gap-2 mb-6 pb-6 border-b border-border">
                <span className="text-4xl font-black text-foreground">{plan.price}</span>
                <span className="text-sm text-muted-foreground">{plan.sub}</span>
              </div>

              <ul className="space-y-2.5 mb-8 flex-1">
                {plan.features.map(f => (
                  <li key={f} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" />
                    <span className="text-sm text-muted-foreground">{f}</span>
                  </li>
                ))}
              </ul>

              <Button className={`w-full font-bold tracking-wide ${plan.highlight ? "bg-primary text-primary-foreground shadow-[0_4px_14px_oklch(0.28_0.09_240/0.35)] hover:shadow-[0_6px_20px_oklch(0.28_0.09_240/0.45)] transition-shadow" : "bg-white border border-border text-foreground hover:bg-secondary"}`}
                variant={plan.highlight ? "default" : "outline"} onClick={go}>
                {plan.cta}
              </Button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
